import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import PhotoSlot from "./PhotoSlot";
import { MaskedLine, Reveal } from "./motion";

export default function SpecialtySection({
    id,
    number,
    title,
    description,
    icon,
    image,
    alt,
    position,
    gallery,
    galleryUrl,
    index,
}) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start end", "end start"],
    });
    const yNum = useTransform(scrollYProgress, [0, 1], [90, -90]);
    const yImg = useTransform(scrollYProgress, [0, 1], [50, -50]);
    const flip = index % 2 === 1;

    return (
        <section
            id={id}
            ref={ref}
            data-testid={`category-${id}`}
            className="relative overflow-hidden py-24 sm:py-32"
        >
            <motion.span
                style={{ y: yNum }}
                aria-hidden="true"
                className={`ghost-number font-display pointer-events-none absolute top-8 select-none text-[26vw] font-semibold leading-none lg:text-[20vw] ${
                    flip ? "left-[-2%]" : "right-[-2%]"
                }`}
            >
                {number}
            </motion.span>
            <div
                className={`pointer-events-none absolute top-1/3 h-[50vh] w-[45vw] rounded-full bg-[radial-gradient(ellipse_at_center,rgba(212,175,55,0.06),transparent_65%)] ${
                    flip ? "right-[-10%]" : "left-[-10%]"
                }`}
            />

            <div className="relative z-10 mx-auto grid max-w-[1500px] items-center gap-12 px-5 sm:px-10 lg:grid-cols-12 lg:gap-16">
                <div
                    className={`lg:col-span-5 ${flip ? "lg:order-2" : "lg:order-1"}`}
                >
                    <Reveal>
                        <p className="font-mono-lux mb-6 flex items-center gap-4 text-[11px] tracking-[0.4em] text-[#D4AF37]">
                            <span className="inline-block h-px w-10 bg-[#D4AF37]/60" />
                            SPÉCIALITÉ N° {number}
                        </p>
                    </Reveal>
                    <h2
                        data-testid={`category-title-${id}`}
                        className="font-display text-3xl font-medium tracking-tight text-white sm:text-4xl lg:text-5xl"
                    >
                        <MaskedLine delay={0.1}>{title}</MaskedLine>
                    </h2>
                    <Reveal delay={0.25}>
                        <div className="mt-7 flex items-center gap-3">
                            <span className="h-px w-16 bg-gradient-to-r from-[#D4AF37] to-transparent" />
                            <span className="h-1.5 w-1.5 rotate-45 bg-[#D4AF37]" />
                        </div>
                        <p className="mt-7 max-w-md text-base font-light leading-relaxed text-[#E2E2E2]/85 sm:text-lg">
                            {description}
                        </p>
                        {gallery &&
                            (galleryUrl ? (
                                <a
                                    href={galleryUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid={`gallery-button-${id}`}
                                    className="group mt-9 inline-flex items-center gap-3 border border-[#D4AF37]/40 bg-transparent px-7 py-3.5 font-mono-lux text-[10px] tracking-[0.28em] text-[#D4AF37] transition-all duration-500 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 hover:shadow-[0_0_30px_rgba(212,175,55,0.18)] sm:text-[11px]"
                                >
                                    {gallery}
                                    <ArrowUpRight
                                        size={15}
                                        strokeWidth={1.6}
                                        className="transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                                    />
                                </a>
                            ) : (
                                <button
                                    type="button"
                                    data-testid={`gallery-button-${id}`}
                                    className="group mt-9 inline-flex items-center gap-3 border border-[#D4AF37]/40 bg-transparent px-7 py-3.5 font-mono-lux text-[10px] tracking-[0.28em] text-[#D4AF37] transition-all duration-500 hover:border-[#D4AF37] hover:bg-[#D4AF37]/10 hover:shadow-[0_0_30px_rgba(212,175,55,0.18)] sm:text-[11px]"
                                >
                                    {gallery}
                                    <ArrowUpRight
                                        size={15}
                                        strokeWidth={1.6}
                                        className="transition-transform duration-500 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                                    />
                                </button>
                            ))}
                    </Reveal>
                </div>

                <motion.div
                    style={{ y: yImg }}
                    className={`lg:col-span-7 ${flip ? "lg:order-1" : "lg:order-2"}`}
                >
                    <Reveal delay={0.15} y={70}>
                        <PhotoSlot
                            icon={icon}
                            label="VOTRE PHOTO ICI"
                            sub="EMPLACEMENT À REMPLACER PAR VOTRE PHOTOGRAPHIE"
                            chip={`${number} — ${title}`}
                            testId={`photo-slot-${id}`}
                            image={image}
                            alt={alt}
                            position={position}
                        />
                    </Reveal>
                </motion.div>
            </div>
        </section>
    );
}
